import { useParams, Link } from "react-router-dom";

import Layout from '../../components/Layout.js';
import ErrorPage from "../errors/ErrorPage.js";

import { applications, websites, games } from './ProjectData.js';

import './ProjectPage.css';

function getCategory(project) {
	if (applications.find(p => p.path === project.path)) return { label: 'Applications', anchor: 'applications' };
	if (websites.find(p => p.path === project.path)) return { label: 'Websites', anchor: 'websites' };
	return { label: 'Games', anchor: 'games' };
}

function ProjectPage() {
	const { slug } = useParams();
	const allProjects = [...applications, ...websites, ...games];
	const idx = allProjects.findIndex(p => p.path === slug);
	const project = allProjects[idx];

	if (!project) return <ErrorPage />;

	const category = getCategory(project);
	const prev = allProjects[idx - 1] ?? null;
	const next = allProjects[idx + 1] ?? null;

	return (
		<Layout pageIndex={3}>
			<div className="pp-outer">

				{/* ── HERO ───────────────────────────────────────── */}
				<div className="pp-hero">
					<img
						src={project.image}
						alt={project.title}
						className="pp-hero-img"
					/>
					<div className="pp-hero-fade" aria-hidden="true" />
					<div className="pp-hero-overlay">
						<Link to="/projects" className="pp-back-link">
							← Back to Projects
						</Link>
						<div>
							<span className="project-tag">{category.label}</span>
							{project.year && (
								<span className="project-tag pp-tag-year">{project.year}</span>
							)}
							<h1 className="pp-title">{project.title}</h1>
						</div>
					</div>
				</div>

				{/* ── BODY ───────────────────────────────────────── */}
				<div className="pp-body">

					{/* ── LEFT COLUMN ──────────────────────────── */}
					<div className="pp-main">

						{/* Description */}
						<section className="pp-section">
							<p className="section-eyebrow">Overview</p>
							<p className="pp-description">{project.description}</p>
						</section>

						{/* Highlights */}
						{project.highlights?.length > 0 && (
							<section className="pp-section">
								<p className="section-eyebrow">Highlights</p>
								<ul className="pp-highlights">
									{project.highlights.map(h => (
										<li key={h} className="pp-highlight-item">{h}</li>
									))}
								</ul>
							</section>
						)}
					</div>

					{/* ── RIGHT SIDEBAR ────────────────────────── */}
					<aside className="pp-sidebar">

						{/* Links */}
						<div className="pp-sidebar-block">
							<p className="pp-sidebar-label">Links</p>
							<div className="pp-link-stack">
								{project.github && (
									<a
										href={project.github}
										target="_blank"
										rel="noopener noreferrer"
										className="pp-link-btn pp-link-github"
									>
										<ion-icon name="logo-github" />
										View on GitHub
									</a>
								)}
								{project.live && (
									<a
										href={project.live}
										target="_blank"
										rel="noopener noreferrer"
										className="pp-link-btn pp-link-live"
									>
										<ion-icon name="open-outline" />
										Live demo
									</a>
								)}
								{!project.github && !project.live && (
									<span className="pp-no-links">No public links available</span>
								)}
							</div>
						</div>

						{/* Tech stack */}
						{project.tags?.length > 0 && (
							<div className="pp-sidebar-block">
								<p className="pp-sidebar-label">Tech stack</p>
								<div className="pp-tag-grid">
									{project.tags.map(t => (
										<span className="tech-pill" key={t}>{t}</span>
									))}
								</div>
							</div>
						)}

						{/* Year */}
						{project.year && (
							<div className="pp-sidebar-block">
								<p className="pp-sidebar-label">Year</p>
								<p className="pp-sidebar-value">{project.year}</p>
							</div>
						)}
					</aside>
				</div>

				{/* ── PREV / NEXT ─────────────────────────────────── */}
				<div className="pp-pagination">
					{prev ? (
						<Link to={`/projects/${prev.path}`} className="pp-page-link pp-page-prev">
							<span className="pp-page-dir">← Previous</span>
							<span className="pp-page-name">{prev.title}</span>
						</Link>
					) : <div />}
					{next ? (
						<Link to={`/projects/${next.path}`} className="pp-page-link pp-page-next">
							<span className="pp-page-dir">Next →</span>
							<span className="pp-page-name">{next.title}</span>
						</Link>
					) : <div />}
				</div>

			</div>
		</Layout>
	);
}

export default ProjectPage;